import React, { useState, useRef, useMemo } from 'react';
import { TOOL_SUGGESTIONS, TOOL_ALIASES } from '../../constants/tags';
import { X, Search } from 'lucide-react';

interface ToolSoftTagInputProps {
    selectedTools: string[]; // array of tool names (free-form strings)
    onChange: (tools: string[]) => void;
    label?: string;
    placeholder?: string;
}

/**
 * Normalize a tool name for deduplication.
 * e.g. "MJ" and "Midjourney" should be treated as the same.
 */
function getCanonicalTool(name: string): string {
    const lower = name.toLowerCase().trim();
    for (const [canonical, aliases] of Object.entries(TOOL_ALIASES)) {
        if (canonical.toLowerCase() === lower) return canonical;
        for (const alias of aliases) {
            if (alias.toLowerCase() === lower) return canonical;
        }
    }
    return name.trim();
}

export const ToolSoftTagInput: React.FC<ToolSoftTagInputProps> = ({
    selectedTools,
    onChange,
    label = '工具/模型',
    placeholder = '输入工具名...'
}) => {
    const [inputValue, setInputValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Deduplicate selected tools using canonical names
    const dedupedTools = useMemo(() => {
        const seen = new Set<string>();
        const result: string[] = [];
        for (const tool of selectedTools) {
            const canonical = getCanonicalTool(tool);
            if (!seen.has(canonical.toLowerCase())) {
                seen.add(canonical.toLowerCase());
                result.push(tool); // keep the user's original display name
            }
        }
        return result;
    }, [selectedTools]);

    // Filter suggestions based on input
    const suggestions = useMemo(() => {
        const query = inputValue.trim().toLowerCase();
        if (!query) return [];
        const selectedCanonicals = new Set(dedupedTools.map(getCanonicalTool).map(t => t.toLowerCase()));
        return TOOL_SUGGESTIONS.filter(tool => {
            const canonical = getCanonicalTool(tool).toLowerCase();
            if (selectedCanonicals.has(canonical)) return false;
            return tool.toLowerCase().includes(query);
        }).slice(0, 8);
    }, [inputValue, dedupedTools]);

    // Popular tools not yet selected
    const popularSuggestions = useMemo(() => {
        const selectedCanonicals = new Set(dedupedTools.map(getCanonicalTool).map(t => t.toLowerCase()));
        return TOOL_SUGGESTIONS.filter(tool => {
            const canonical = getCanonicalTool(tool).toLowerCase();
            return !selectedCanonicals.has(canonical);
        }).slice(0, 6);
    }, [dedupedTools]);

    const addTool = (toolName: string) => {
        const trimmed = toolName.trim();
        if (!trimmed) return;
        const canonical = getCanonicalTool(trimmed).toLowerCase();
        const exists = dedupedTools.some(t => getCanonicalTool(t).toLowerCase() === canonical);
        if (!exists) {
            onChange([...dedupedTools, trimmed]);
        }
        setInputValue('');
        setShowSuggestions(false);
        inputRef.current?.focus();
    };

    const removeTool = (index: number) => {
        onChange(dedupedTools.filter((_, i) => i !== index));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (suggestions.length > 0) {
                addTool(suggestions[0]);
            } else {
                addTool(inputValue);
            }
        } else if (e.key === 'Backspace' && inputValue === '' && dedupedTools.length > 0) {
            onChange(dedupedTools.slice(0, -1));
        }
    };

    // Click outside to close
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="space-y-2" ref={wrapperRef}>
            {label && (
                <label className="text-sm font-medium text-slate-700 block">
                    {label}
                    <span className="text-slate-400 font-normal ml-2 text-xs">可选，自由输入</span>
                </label>
            )}

            {/* Input with selected tags */}
            <div className="relative">
                <div
                    className="flex flex-wrap gap-2 p-2 min-h-[42px] bg-white border border-slate-200 rounded-lg focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-400 transition-all cursor-text"
                    onClick={() => inputRef.current?.focus()}
                >
                    {dedupedTools.map((tool, idx) => (
                        <span
                            key={`${tool}-${idx}`}
                            className="inline-flex items-center px-2 py-1 rounded bg-purple-50 text-purple-700 text-sm border border-purple-100"
                        >
                            {tool}
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); removeTool(idx); }}
                                className="ml-1 hover:text-purple-900 focus:outline-none"
                            >
                                <X size={14} />
                            </button>
                        </span>
                    ))}

                    <div className="flex items-center flex-1 min-w-[120px]">
                        <Search className="w-4 h-4 text-slate-300 mr-1.5 shrink-0" />
                        <input
                            ref={inputRef}
                            type="text"
                            className="flex-1 outline-none bg-transparent text-sm py-1"
                            placeholder={dedupedTools.length === 0 ? placeholder : ''}
                            value={inputValue}
                            onChange={(e) => {
                                setInputValue(e.target.value);
                                setShowSuggestions(true);
                            }}
                            onKeyDown={handleKeyDown}
                            onFocus={() => setShowSuggestions(true)}
                            autoComplete="off"
                        />
                    </div>
                </div>

                {/* Suggestions dropdown */}
                {showSuggestions && (inputValue.trim() || dedupedTools.length === 0) && (
                    <div className="absolute top-full left-0 z-50 w-full max-w-md mt-1 bg-white rounded-lg shadow-xl border border-slate-100 overflow-hidden max-h-60 overflow-y-auto">
                        {inputValue.trim() && suggestions.length === 0 && (
                            <div
                                className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer text-sm text-purple-600 flex items-center gap-2"
                                onClick={() => addTool(inputValue)}
                            >
                                添加 "{inputValue.trim()}"
                            </div>
                        )}

                        {suggestions.length > 0 && (
                            <ul>
                                {suggestions.map((tool) => (
                                    <li
                                        key={tool}
                                        className="px-4 py-2 hover:bg-slate-50 cursor-pointer text-sm text-slate-700 flex items-center justify-between"
                                        onClick={() => addTool(tool)}
                                    >
                                        <span>{tool}</span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {!inputValue.trim() && popularSuggestions.length > 0 && (
                            <div className="p-3">
                                <p className="text-xs text-slate-400 mb-2">热门工具:</p>
                                <div className="flex flex-wrap gap-2">
                                    {popularSuggestions.map(tool => (
                                        <button
                                            key={tool}
                                            type="button"
                                            onClick={() => addTool(tool)}
                                            className="text-xs px-2.5 py-1 bg-slate-50 text-slate-600 rounded-full hover:bg-purple-50 hover:text-purple-600 hover:border-purple-100 border border-transparent transition-colors"
                                        >
                                            {tool}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
